import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useQueuedPatientUpdate } from "@louise/patients/sync/usePatientSync";
import type { Patient } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { buildMenstrualHealthNavigationStack } from "./buildMenstrualHealthNavigationStack";
import { deriveCycleLengthRange } from "./deriveCycleLengthRange";
import { transformMenstrualHealthStateToRequest } from "./transformMenstrualHealthStateToRequest";
import type { MenstrualHealthFlowRoute } from "./buildMenstrualHealthNavigationStack";
import type { CycleStatus, FlowIntensity, SymptomType } from "./options";

export interface SymptomDetail {
  id?: string;
  symptom: SymptomType;
  other?: string | null;
}

export interface MenstrualHealthState {
  id?: string;
  cycleStatus: CycleStatus | null;
  ageAtFirstPeriod: number | null;
  minAverageCycleLength: number | null;
  maxAverageCycleLength: number | null;
  menstrualFlowIntensity: FlowIntensity | null;
  period1StartDate: string | null;
  period1EndDate: string | null;
  period2StartDate: string | null;
  period2EndDate: string | null;
  period3StartDate: string | null;
  period3EndDate: string | null;
  bleedsOutsidePeriod: boolean | null;
  painScale: number | null;
  takesPainMedication: boolean | null;
  hasPeriodSymptoms: boolean | null;
  awareOfFertileWindow: boolean | null;
  symptoms: SymptomDetail[];
  symptomIdsToDelete: string[];
  navigationStack: MenstrualHealthFlowRoute[];
  currentNavigationIndex: number;
}

const initialState: MenstrualHealthState = {
  cycleStatus: null,
  ageAtFirstPeriod: null,
  minAverageCycleLength: null,
  maxAverageCycleLength: null,
  menstrualFlowIntensity: null,
  period1StartDate: null,
  period1EndDate: null,
  period2StartDate: null,
  period2EndDate: null,
  period3StartDate: null,
  period3EndDate: null,
  bleedsOutsidePeriod: null,
  painScale: null,
  takesPainMedication: null,
  hasPeriodSymptoms: null,
  awareOfFertileWindow: null,
  symptoms: [],
  symptomIdsToDelete: [],
  navigationStack: ["CycleStatus"],
  currentNavigationIndex: 0,
};

/**
 * Determine which screen to resume from based on patient data.
 * Returns the first screen in the flow that hasn't been completed.
 */
export function getResumeRoute(
  patient: Patient | null | undefined,
): MenstrualHealthFlowRoute {
  const mh = patient?.obGynHistory?.menstrualHealth;
  if (!mh || mh.cycleStatus == null) return "CycleStatus";

  // Build a temporary state from patient data to determine the flow
  const inferredCycleLength = deriveCycleLengthRange([
    mh.period1StartDate,
    mh.period2StartDate,
    mh.period3StartDate,
  ]);
  const tempState: MenstrualHealthState = {
    ...initialState,
    cycleStatus: mh.cycleStatus ?? null,
    ageAtFirstPeriod: mh.ageAtFirstPeriod ?? null,
    minAverageCycleLength:
      inferredCycleLength?.minAverageCycleLength ??
      mh.minAverageCycleLength ??
      null,
    maxAverageCycleLength:
      inferredCycleLength?.maxAverageCycleLength ??
      mh.maxAverageCycleLength ??
      null,
    menstrualFlowIntensity: mh.menstrualFlowIntensity ?? null,
    period1StartDate: mh.period1StartDate ?? null,
    period1EndDate: mh.period1EndDate ?? null,
    period2StartDate: mh.period2StartDate ?? null,
    period2EndDate: mh.period2EndDate ?? null,
    period3StartDate: mh.period3StartDate ?? null,
    period3EndDate: mh.period3EndDate ?? null,
    bleedsOutsidePeriod: mh.bleedsOutsidePeriod ?? null,
    painScale: mh.painScale ?? null,
    takesPainMedication: mh.takesPainMedication ?? null,
    hasPeriodSymptoms: mh.hasPeriodSymptoms ?? null,
    awareOfFertileWindow: mh.awareOfFertileWindow ?? null,
    symptoms: (mh.symptoms ?? []).map((s) => ({
      symptom: s?.symptom ?? "other",
      other: s?.other ?? null,
    })),
  };

  const stack = buildMenstrualHealthNavigationStack(tempState);

  const screenComplete: Record<MenstrualHealthFlowRoute, () => boolean> = {
    CycleStatus: () => tempState.cycleStatus !== null,
    AgeAtFirstPeriod: () => tempState.ageAtFirstPeriod !== null,
    // Retained for compatibility with older deep links. It is no longer in
    // the active stack because cycle length is derived from period dates.
    CycleDetails: () => true,
    Last3Periods: () => tempState.period1StartDate !== null,
    BleedOutsidePeriod: () => tempState.bleedsOutsidePeriod !== null,
    PainScale: () => tempState.painScale !== null,
    PainMedication: () => tempState.takesPainMedication !== null,
    Symptoms: () => tempState.hasPeriodSymptoms !== null,
    FertileWindow: () => tempState.awareOfFertileWindow !== null,
    MenstrualHealthComplete: () => false,
  };

  for (const route of stack) {
    if (route === "MenstrualHealthComplete") continue;
    if (!screenComplete[route]()) return route;
  }

  // All screens complete — start from the beginning so users can review
  return stack[0] ?? "CycleStatus";
}

interface MenstrualHealthContextValue {
  state: MenstrualHealthState;
  updateState: (updates: Partial<MenstrualHealthState>) => void;
  resetState: () => void;
  /** Save current state to API (fire-and-forget) */
  save: (stateOverride?: Partial<MenstrualHealthState>) => Promise<void>;
}

const MenstrualHealthContext =
  createContext<MenstrualHealthContextValue | null>(null);

export function MenstrualHealthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const { mutateAsync } = useQueuedPatientUpdate();
  const [state, setState] = useState<MenstrualHealthState>(initialState);

  // Load data from patient
  useEffect(() => {
    if (isLoading || !patient) return;

    const menstrualHealth = patient?.obGynHistory?.menstrualHealth;
    if (!menstrualHealth) return;

    setState((prev) => {
      const isInitialState =
        prev.cycleStatus === null && prev.symptoms.length === 0;

      if (!isInitialState) return prev;

      const symptoms: SymptomDetail[] = (menstrualHealth.symptoms ?? []).map(
        (s) => ({
          id: s?.id ?? undefined,
          symptom: s?.symptom ?? "other",
          other: s?.other ?? null,
        }),
      );
      const inferredCycleLength = deriveCycleLengthRange([
        menstrualHealth.period1StartDate,
        menstrualHealth.period2StartDate,
        menstrualHealth.period3StartDate,
      ]);

      const next: MenstrualHealthState = {
        ...prev,
        id: menstrualHealth.id ?? undefined,
        cycleStatus: menstrualHealth.cycleStatus ?? null,
        ageAtFirstPeriod: menstrualHealth.ageAtFirstPeriod ?? null,
        minAverageCycleLength:
          inferredCycleLength?.minAverageCycleLength ??
          menstrualHealth.minAverageCycleLength ??
          null,
        maxAverageCycleLength:
          inferredCycleLength?.maxAverageCycleLength ??
          menstrualHealth.maxAverageCycleLength ??
          null,
        menstrualFlowIntensity: menstrualHealth.menstrualFlowIntensity ?? null,
        period1StartDate: menstrualHealth.period1StartDate ?? null,
        period1EndDate: menstrualHealth.period1EndDate ?? null,
        period2StartDate: menstrualHealth.period2StartDate ?? null,
        period2EndDate: menstrualHealth.period2EndDate ?? null,
        period3StartDate: menstrualHealth.period3StartDate ?? null,
        period3EndDate: menstrualHealth.period3EndDate ?? null,
        bleedsOutsidePeriod: menstrualHealth.bleedsOutsidePeriod ?? null,
        painScale: menstrualHealth.painScale ?? null,
        takesPainMedication: menstrualHealth.takesPainMedication ?? null,
        hasPeriodSymptoms: menstrualHealth.hasPeriodSymptoms ?? null,
        awareOfFertileWindow: menstrualHealth.awareOfFertileWindow ?? null,
        symptoms,
      };

      const stack = buildMenstrualHealthNavigationStack(next);

      return {
        ...next,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          prev.currentNavigationIndex,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, [patient, isLoading]);

  const updateState = useCallback((updates: Partial<MenstrualHealthState>) => {
    setState((prev) => {
      const newState = { ...prev, ...updates };
      const stack = buildMenstrualHealthNavigationStack(newState);
      return {
        ...newState,
        navigationStack: stack,
        currentNavigationIndex: Math.min(
          newState.currentNavigationIndex ?? 0,
          Math.max(stack.length - 1, 0),
        ),
      };
    });
  }, []);

  const save = useCallback(
    async (stateOverride?: Partial<MenstrualHealthState>) => {
      const saveState = stateOverride ? { ...state, ...stateOverride } : state;
      const payload = transformMenstrualHealthStateToRequest(saveState);
      if (Object.keys(payload).length === 0) return;

      // Include the obGynHistory ID so the API updates the existing record
      const obGynHistoryId = patient?.obGynHistory?.id;
      if (payload.obGynHistory && obGynHistoryId) {
        payload.obGynHistory.id = obGynHistoryId;
      }

      try {
        await mutateAsync(payload);
      } catch (error) {
        throw error;
      }
    },
    [state, mutateAsync, patient],
  );

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <MenstrualHealthContext.Provider
      value={{ state, updateState, resetState, save }}
    >
      {children}
    </MenstrualHealthContext.Provider>
  );
}

export function useMenstrualHealth() {
  const ctx = useContext(MenstrualHealthContext);
  if (!ctx) {
    throw new Error(
      "useMenstrualHealth must be used within MenstrualHealthProvider",
    );
  }
  return ctx;
}
